import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";

const KINDS = [
  { id: "all", label: "All" },
  { id: "wingman", label: "Tam" },
  { id: "content", label: "Content" },
  { id: "images", label: "Images" },
];

const Vault = () => {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState({ wingman: [], content: [], images: [] });

  const search = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (kind !== "all") params.kind = kind;
    const { data } = await api.get("/vault/search", { params });
    setData(data);
  };

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <div className="space-y-6" data-testid="vault-panel">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Search your Vault — chats, posts, images…"
            data-testid="vault-search-input"
            className="w-full pl-9 pr-4 py-3 bg-[#0A192F] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm"
          />
        </div>
        <button
          onClick={search}
          data-testid="vault-search-btn"
          className="px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-[#33F3FF]"
        >
          Search
        </button>
      </div>

      <div className="flex gap-2">
        {KINDS.map((k) => (
          <button
            key={k.id}
            onClick={() => setKind(k.id)}
            data-testid={`vault-filter-${k.id}`}
            className={[
              "px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-sm border",
              kind === k.id
                ? "bg-cyan-400/10 text-cyan-300 border-cyan-400/40"
                : "text-slate-400 border-white/10 hover:border-white/30",
            ].join(" ")}
          >
            {k.label}
          </button>
        ))}
      </div>

      {(kind === "all" || kind === "wingman") && (
        <Section title="Tam Threads" items={data.wingman || []} render={(t) => (
          <div className="p-4 bg-[#0A192F] border border-white/10 rounded-sm" data-testid={`vault-wingman-${t.thread_id}`}>
            <p className="text-white font-medium">{t.title}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              {t.personality} · {new Date(t.updated_at).toLocaleString()}
            </p>
          </div>
        )}/>
      )}

      {(kind === "all" || kind === "content") && (
        <Section title="Content Drafts" items={data.content || []} render={(p) => (
          <div className="p-4 bg-[#0A192F] border border-white/10 rounded-sm" data-testid={`vault-content-${p.post_id}`}>
            <p className="text-white font-medium">{p.title}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">
              {p.tone} · {new Date(p.created_at).toLocaleString()}
            </p>
          </div>
        )}/>
      )}

      {(kind === "all" || kind === "images") && (
        <Section title="Images" items={data.images || []} render={(i) => (
          <div className="bg-[#0A192F] border border-white/10 rounded-sm overflow-hidden" data-testid={`vault-image-${i.image_id}`}>
            {i.data_url && <img src={i.data_url} alt={i.prompt} className="w-full aspect-square object-cover" />}
            <p className="p-2 text-[11px] text-slate-400 line-clamp-2">{i.prompt}</p>
          </div>
        )} grid />
      )}
    </div>
  );
};

const Section = ({ title, items, render, grid }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">{title}</p>
    {items.length === 0 ? (
      <p className="text-slate-500 text-sm">Nothing here yet.</p>
    ) : (
      <div className={grid ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" : "space-y-2"}>
        {items.map((it, i) => (
          <div key={i}>{render(it)}</div>
        ))}
      </div>
    )}
  </div>
);

export default Vault;
